import { useState } from 'react';
import {
  testimonials as Api, type Testimonial, type TestimonialInput, type PubState,
} from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { Shim } from '../components/Shimmer';
import { useSelection, TickAll, Tick, BulkActions } from '../components/Bulk';
import { Modal } from '../components/Modal';
import { ImagePick } from '../components/ImagePick';
import { Field, Row, StateTag } from '../components/Field';

/**
 * What learners say, on the home page.
 *
 * A quote is a claim made to buyers on the desk's behalf, so the screen says
 * plainly which rows are the seeded samples and that the band stays off the
 * public page until a real one is published.
 */
export function Testimonials() {
  const { data, error, loading, reload } = useAsync((signal) => Api.list(signal));
  const [editing, setEditing] = useState<Testimonial | 'new' | null>(null);
  const rows = data?.items ?? [];
  const sel = useSelection(rows);
  const live = rows.filter((t) => t.state === 'published').length;
  const samples = rows.filter((t) => /\(sample\)/i.test(t.name)).length;

  async function move(row: Testimonial, by: number) {
    const ordered = [...rows];
    const i = ordered.findIndex((t) => t.id === row.id);
    const to = i + by;
    if (i === -1 || to < 0 || to >= ordered.length) return;
    const [moved] = ordered.splice(i, 1);
    if (!moved) return;
    ordered.splice(to, 0, moved);
    await Promise.all(ordered.map((t, n) => (t.sort === n + 1 ? null : Api.update(t.id, {
      name: t.name, place: t.place, detail: t.detail, quote: t.quote, rating: t.rating,
      photo_media_id: t.photo_media_id, state: t.state, sort: n + 1,
    }))).filter(Boolean));
    await reload();
  }

  async function remove(row: Testimonial) {
    if (!confirm(`Delete the testimonial from “${row.name}”?`)) return;
    await Api.remove(row.id);
    await reload();
  }

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Testimonials</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            {loading && !data ? <Shim w={230} h={12} style={{ marginTop: 3 }} />
              : `${live} on the home page, ${rows.length} in all`}
            {' · '}the heading above them is on the Landing screen
          </p>
        </div>
        <button className="b bg" type="button" onClick={() => setEditing('new')}>New testimonial</button>
      </div>

      {samples > 0 ? (
        <div className="crd" style={{
          background: "rgba(var(--goldrgb), 0.05)",
          border: "1px solid rgba(var(--goldrgb), 0.22)",
          borderRadius: 12,
          padding: "15px 18px",
          marginBottom: 16,
          fontSize: 13.5,
          lineHeight: 1.6,
          display: "flex",
          alignItems: "flex-start",
          gap: 12
        }}>
          <span style={{ fontSize: 16, lineHeight: 1, marginTop: 2 }}>💡</span>
          <div>
            <strong style={{ color: "var(--gold)", display: "inline-block", marginRight: 6 }}>
              {samples} sample{samples === 1 ? "" : "s"} — not real reviews.
            </strong>
            <span style={{ color: "var(--tx2)" }}>
              They are drafts so the band can be seen in the Landing preview. Replace each with a real
              learner's words (with their permission), then publish. Publishing a made-up testimonial
              on the live site is a fake review.
            </span>
          </div>
        </div>
      ) : null}

      {error ? <div className="err">{error.message}</div> : null}

      <BulkActions
        path="/testimonials" noun="testimonial"
        rows={sel.picked.map((t) => ({ id: t.id, label: t.name }))}
        actions={[['publish', 'Publish'], ['unpublish', 'Unpublish']]}
        onDone={async () => { sel.clear(); await reload(); }}
      />

      <div className="crd" style={{ padding: 0 }}>
        <div className="tblx">
          <table className="tbl">
            <thead>
              <tr>
                <TickAll on={sel.allOn} some={sel.picked.length > 0} onChange={sel.toggleAll} />
                <th>Learner</th><th>Rating</th><th>State</th><th>Order</th><th />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="muted" style={{ padding: '26px 8px' }}>
                  No testimonials yet. The band stays off the home page until one is published.
                </td></tr>
              ) : rows.map((t, i) => (
                <tr key={t.id} style={sel.isOn(t.id) ? { background: 'rgba(var(--goldrgb), .05)' } : undefined}>
                  <Tick on={sel.isOn(t.id)} label={t.name} onChange={(v) => sel.toggle(t.id, v)} />
                  <td>
                    <div style={{ fontWeight: 700 }}>{t.name}
                      <span style={{ fontWeight: 400, color: 'var(--tx3)' }}>
                        {[t.detail, t.place].filter(Boolean).length ? ` · ${[t.detail, t.place].filter(Boolean).join(' · ')}` : ''}
                      </span>
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--tx3)', maxWidth: 560, overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.quote}</div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{t.rating ? `${'★'.repeat(t.rating)}` : '—'}</td>
                  <td><StateTag state={t.state} /></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="b bo" type="button" aria-label={`Move ${t.name} up`}
                      style={{ padding: '3px 9px', fontSize: 12 }}
                      disabled={i === 0} onClick={() => void move(t, -1)}>↑</button>
                    <button className="b bo" type="button" aria-label={`Move ${t.name} down`}
                      style={{ padding: '3px 9px', fontSize: 12, marginLeft: 5 }}
                      disabled={i === rows.length - 1} onClick={() => void move(t, 1)}>↓</button>
                  </td>
                  <td className="right" style={{ whiteSpace: 'nowrap' }}>
                    <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5 }}
                      type="button" onClick={() => setEditing(t)}>Edit</button>
                    <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5, marginLeft: 6 }}
                      type="button" onClick={() => void remove(t)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing ? (
        <TestimonialForm
          row={editing === 'new' ? null : editing}
          nextSort={rows.length + 1}
          onCancel={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      ) : null}
    </div>
  );
}

function TestimonialForm({ row, nextSort, onCancel, onSaved }: {
  row: Testimonial | null;
  nextSort: number;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [f, setF] = useState<TestimonialInput>(() => row
    ? {
      name: row.name, place: row.place, detail: row.detail, quote: row.quote, rating: row.rating,
      photo_media_id: row.photo_media_id, sort: row.sort, state: row.state,
    }
    : { name: '', place: '', detail: '', quote: '', rating: 5, photo_media_id: null, sort: nextSort, state: 'draft' });
  const [photo, setPhoto] = useState<string | null>(row?.photo_path ?? null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const set = <K extends keyof TestimonialInput>(k: K, v: TestimonialInput[K]) => setF((p) => ({ ...p, [k]: v }));

  async function submit(state: PubState) {
    if (!f.name.trim()) { setError('A name is required.'); return; }
    if (!f.quote.trim()) { setError('The quote is required.'); return; }
    if (state === 'published' && /\(sample\)/i.test(f.name)
      && !confirm('This is still marked as a sample. Publish it on the live home page anyway?')) return;
    setError('');
    setStatus(state === 'published' ? 'Publishing…' : 'Saving…');
    try {
      if (row) await Api.update(row.id, { ...f, state });
      else await Api.create({ ...f, state });
      await onSaved();
    } catch (e) { setStatus(''); setError((e as Error).message); }
  }

  return (
    <Modal
      title={row ? 'Edit testimonial' : 'New testimonial'}
      onClose={onCancel}
      footer={
        <>
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{status}</span>
          <span className="mdl-spacer" />
          <button className="b bo" type="button" onClick={onCancel}>Cancel</button>
          <button className="b bo" type="button" onClick={() => void submit('draft')}>Save draft</button>
          <button className="b bg" type="button" onClick={() => void submit('published')}>
            {row?.state === 'published' ? 'Save' : 'Publish'}
          </button>
        </>
      }
    >
      {error ? <div className="err">{error}</div> : null}
      <Row cols={2}>
        <Field label="Name" hint="As they agreed to be named — a first name and initial is fine.">
          <input className="inp" value={f.name} autoFocus onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="Town or city">
          <input className="inp" value={f.place} onChange={(e) => set('place', e.target.value)} />
        </Field>
      </Row>
      <Field label="What they took" hint="For example “Learner · Time Trader” or “Live class attendee”.">
        <input className="inp" value={f.detail} onChange={(e) => set('detail', e.target.value)} />
      </Field>
      <Field label="Their words" hint="Their own words, with their permission. No profit figures or promised returns.">
        <textarea className="inp ta" rows={5} value={f.quote} onChange={(e) => set('quote', e.target.value)} />
      </Field>
      <Row cols="160px 120px">
        <Field label="Rating">
          <select className="inp" value={f.rating} onChange={(e) => set('rating', Number(e.target.value))}>
            <option value={0}>No stars</option>
            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{'★'.repeat(n)}</option>)}
          </select>
        </Field>
        <Field label="Order">
          <input className="inp" type="number" value={f.sort} onChange={(e) => set('sort', Number(e.target.value))} />
        </Field>
      </Row>
      <ImagePick label="Photo (optional)" path={photo} alt={f.name} ratio="1 / 1"
        hint="A square photo of the learner, with their permission. Without one, their initials are shown."
        onPick={(id, p) => { set('photo_media_id', id); setPhoto(p); }} />
    </Modal>
  );
}
